import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { UserModel } from 'src/app/models/user.model';
import { UtilsService } from 'src/app/shared/utils.service';
import { ContractorService } from '../contractor.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { RBACService } from 'src/app/shared/rbac.service';

@Component({
  selector: 'app-contractors-layout',
  templateUrl: './contractors-layout.component.html',
  styleUrls: ['./contractors-layout.component.scss'],
  providers: [ContractorService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractorsLayoutComponent implements OnInit {
  contractor: UserModel;

  // Permission flags resolved ONCE in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess()/app.planCan() on every change detection cycle.
  canSendMessage: boolean = false;
  canViewSafetyPlanSites: boolean = false;
  canViewContractorDocuments: boolean = false;
  canAccessPreQualifications: boolean = false;
  canViewSubcontractors: boolean = false;
  canAccessContractorEmployees: boolean = false;
  canEditContractors: boolean = false;

  constructor(
    private route: ActivatedRoute,
    public app: AppService,
    public contractorService: ContractorService,
    private router: Router,
    public utils: UtilsService,
    private rbac: RBACService
  ) {}

  // Expose RBACService and AppService to template
  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;

  ngOnInit() {
    // Resolve the permission flags first so they are always set, even when an early return below fires.
    this.canSendMessage =
      this.app.planCan(AppService.FEATURES.MESSAGES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      );

    this.canViewSafetyPlanSites =
      this.app.planCan(AppService.FEATURES.SAFETY_PLANS) &&
      (this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      ) ||
        this.rbac.userCanAccess([
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_SAFETY_PLANS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_SAFETY_PLANS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_SAFETY_PLANS,
            RBACService.RBAC_A.A_DELETE
          )
        ]));

    this.canViewContractorDocuments =
      this.app.planCan(AppService.FEATURES.DOCUMENTS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

    this.canAccessPreQualifications =
      this.app.planCan(AppService.FEATURES.PRE_QUALIFICATIONS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_EDIT
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ]);

    this.canViewSubcontractors = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_SUBCONTRACTORS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    this.canAccessContractorEmployees = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_CONTRACTORS_EMPLOYEES,
        RBACService.RBAC_A.A_CREATE
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_CONTRACTORS_EMPLOYEES,
        RBACService.RBAC_A.A_VIEW
      )
    ]);

    this.canEditContractors = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // Get the contractor ID
    const contractorId = Number(this.route.snapshot.params['contractor_id']);
    if (!contractorId) {
      // Show Error
      this.router.navigate(['/contractors']);
      return;
    }

    this.contractorService.shouldRedirectOnFailure = true;
    this.contractorService.getContractorById(contractorId);
  }

  composeNewMessage(contractorId: number) {
    if (!contractorId) {
      this.utils.showToast(
        'Unable to open message composer. Please try again.'
      );
      return; // early termination
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectAccountIds: [contractorId]
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }
}
